import { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Typography, TextField, Button, IconButton, Tooltip,
} from '@mui/material';
import { useIsMobile } from '../../hooks/use-is-mobile';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import OpenInNewIcon   from '@mui/icons-material/OpenInNew';
import CheckIcon       from '@mui/icons-material/Check';
import CloseIcon       from '@mui/icons-material/Close';

/**
 * Props:
 *  open        {boolean}
 *  onClose     {() => void}
 *  url         {string}   — URL completa del invite
 *  title       {string}   — Título del dialog
 *  description {string}   — Texto explicativo
 */
export function InviteLinkDialog({ open, onClose, url, title, description }) {
  const [copied, setCopied] = useState(false);
  const isMobile = useIsMobile();

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // fallback para navegadores sin clipboard API
      const el = document.createElement('textarea');
      el.value = url;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth fullScreen={isMobile}>
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pb: 1,
          fontWeight: 700,
          color: '#284B32',
        }}
      >
        {title}
        <IconButton size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: '8px !important' }}>
        {description && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {description}
          </Typography>
        )}

        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <TextField
            value={url}
            size="small"
            fullWidth
            inputProps={{ readOnly: true, style: { fontSize: '0.82rem', fontFamily: 'monospace' } }}
            onClick={(e) => e.target.select()}
          />
          <Tooltip title={copied ? 'Copiado' : 'Copiar'} placement="top">
            <IconButton
              onClick={handleCopy}
              size="small"
              sx={{
                flexShrink: 0,
                color: copied ? '#0F6E56' : 'text.secondary',
                border: '1px solid',
                borderColor: copied ? '#0F6E56' : 'divider',
                borderRadius: 1,
                transition: 'all 0.2s',
              }}
            >
              {copied ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
          <Tooltip title="Abrir en nueva pestaña" placement="top">
            <IconButton
              component="a"
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              size="small"
              sx={{
                flexShrink: 0,
                color: 'text.secondary',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
              }}
            >
              <OpenInNewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={handleCopy}
          variant="contained"
          size="small"
          startIcon={copied ? <CheckIcon /> : <ContentCopyIcon />}
          sx={{
            backgroundColor: copied ? '#0F6E56' : '#284B32',
            '&:hover': { backgroundColor: copied ? '#0a5a45' : '#1f3a26' },
            textTransform: 'none',
            fontWeight: 600,
            transition: 'background-color 0.2s',
          }}
        >
          {copied ? 'Copiado' : 'Copiar link'}
        </Button>
        <Button onClick={onClose} size="small" sx={{ textTransform: 'none', color: 'text.secondary' }}>
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
}
